/**
 * Sponsorship completion / summary screen.
 *
 * Renders after a sponsorship purchase (status=purchased) or an
 * approval-required submission (status=pending_approval). Shows the
 * confirmation number, an optional fulfillment link (to submit logo /
 * assets later), and the mode-specific handoff (postMessage in popup,
 * return_url CTA in redirect).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import type { ConfigResponse, EmbedClient } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import type { DisplayMode } from '@/App';
import './DonePage.css';

interface Props {
  // Present for prop-shape uniformity with the team DonePage.
  client?: EmbedClient;
  config: ConfigResponse | null;
  displayMode: DisplayMode;
}

export function SponsorDonePage({ config, displayMode }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useFlowNavigate();
  const { sponsorResult } = useFlow();

  const pending = sponsorResult?.status === 'pending_approval';

  // Popup mode: post completion to the host page so loader.js can close
  // the modal. Redirect mode renders the return CTA instead.
  useEffect(() => {
    if (displayMode === 'popup' && sponsorResult) {
      try {
        window.parent.postMessage(
          {
            type: 'tn:sponsorship:complete',
            sponsorship: {
              assignment_id: sponsorResult.assignment_id,
              status: sponsorResult.status,
              confirmation_number: sponsorResult.confirmation_number,
            },
          },
          '*',
        );
      } catch {
        // non-fatal — host just won't auto-close.
      }
    }
  }, [displayMode, sponsorResult]);

  // Landed here without a result (e.g. reload) — send them back to start.
  if (!sponsorResult) {
    navigate(`/events/${eventId}/sponsor`, { replace: true });
    return null;
  }

  // Opened as a script popup window (the embed "popup window" integration —
  // e.g. the Wix button — uses window.open). "Back to organizer" closes the
  // popup to reveal the launching site instead of navigating it away.
  const isPopupWindow =
    typeof window !== 'undefined' && !!window.opener && window.opener !== window;

  return (
    <div className="tn-done">
      <h1 className="tn-done__title">
        {pending ? 'Submitted for approval' : 'Thanks for sponsoring!'}
      </h1>
      <p className="tn-done__subtitle">{sponsorResult.message}</p>

      <div className="tn-card tn-done__summary">
        <div className="tn-done__summary-row">
          <span>Confirmation</span>
          <span>{sponsorResult.confirmation_number}</span>
        </div>
      </div>

      {sponsorResult.fulfillment_url && !sponsorResult.fulfillment_complete ? (
        <p className="tn-done__subtitle">
          Finish setting up your sponsorship (logo, link) any time using the
          link in your confirmation email.
        </p>
      ) : null}

      <div className="tn-done__actions">
        {isPopupWindow ? (
          <button
            className="tn-btn tn-btn--primary"
            type="button"
            onClick={() => window.close()}
          >
            {config?.branding.organizer_name
              ? `Back to ${config.branding.organizer_name}`
              : 'Close'}
          </button>
        ) : displayMode === 'redirect' && config?.return_url ? (
          <a className="tn-btn tn-btn--primary" href={config.return_url}>
            Back to {config.branding.organizer_name}
          </a>
        ) : null}
      </div>
    </div>
  );
}
